import { ComponentProps, FC } from 'react';
import { AutocompleteElement, useFormContext } from 'react-hook-form-mui';

import { useCountries } from '../../../../../services/hooks/useCountries';
import { useTranslation } from 'next-i18next';

interface Props
  extends Omit<ComponentProps<typeof AutocompleteElement>, 'options'> {
  disabled?: boolean;
}

export const CountriesAutocomplete: FC<Props> = (props) => {
  const { autocompleteProps, ...rest } = props;
  const { t } = useTranslation(['directory', 'common']);
  const { setValue } = useFormContext();
  const { data: countriesRes, isLoading } = useCountries();

  const countries = countriesRes?.data || [];

  const groupedOptions = [
    {
      groupName: t('common:panel:countries'),
      options: countries.filter((c) => c.isCafta),
    },
    {
      groupName: t('common:panel:others'),
      options: countries.filter((c) => !c.isCafta),
    },
  ];

  const options = groupedOptions.reduce((acc, group) => {
    const groupOptions = group.options.map((c) => ({
      id: c.id,
      label: c.name,
      isCafta: group.groupName === t('common:panel:countries'),
    }));
    return [...acc, ...groupOptions];
  }, []);

  return (
    <AutocompleteElement
      loading={isLoading}
      options={options}
      autocompleteProps={{
        disabled: props.disabled,
        onChange: (event, value) => {
          setValue(props.name, value?.id);
        },
        getOptionLabel: (option) => {
          if (option.label) {
            return option.label;
          }
          return options.find((c) => c.id === Number(option))?.label;
        },
        groupBy: (option) =>
          option.isCafta
            ? t('common:panel:countries')
            : t('common:panel:others'),
        ...autocompleteProps,
      }}
      {...rest}
    />
  );
};
